import { useL10n } from "@ews/react-localization-context";

import { SystemPlan } from "@ews/react-data";
import { IonButton, IonFab, IonFabButton, IonHeader, IonIcon, IonItem, IonList, IonModal, IonText, IonTitle, IonToolbar } from "@ionic/react";
import { trash } from "ionicons/icons";
import { useEffect, useRef, useState } from "react";
import { floatingPromiseHandler } from "../../../../../../utils";
import SettingsCheckbox from "../../../../../Settings/SettingsComponents/SettingsCeckbox/SettingsCeckbox";
import SettingsDropFile from "../../../../../Settings/SettingsComponents/SettingsDropFile/SettingsDropFile";
import SettingsNumberInput from "../../../../../Settings/SettingsComponents/SettingsNumberInput/SettingsNumberInput";
import SettingsTextInput from "../../../../../Settings/SettingsComponents/SettingsTextInput/SettingsTextInput";
import { hasMainPlan, isMainPlan } from "../../../../planUtils";
import { loadImageBounds } from "../../Floors";
import style from "./FloorEdit.module.scss";
import { FloorEditProps } from "./types";

const FloorEdit: React.FC<FloorEditProps> = ({
    floor: current,
    floors = [],
    systemId,
    onDismiss,
    onDelete,
    onModified
}) =>
{

    const [_floor, setFloor] = useState<SystemPlan | Record<string, any>>({});

    const { translate: t } = useL10n();
    const modal = useRef<HTMLIonModalElement>(null);
    const [error, setError] = useState("");

    const [size, setSize] = useState<{ width?: number, height?: number; }>({});


    function onWillDismiss()
    {
        onDismiss(false);
        close();
    }
    function floorNumberExist(floor: number)
    {
        const _floor = floors.filter(p => p.floor === floor);
        return Boolean(_floor.length);
    }

    function _delete()
    {
        if (onDelete && current?.id) {
            onDelete(current);
        };
        close();
    }

    function close()
    {
        setFloor({});
        modal.current?.dismiss().catch(floatingPromiseHandler(import.meta.url));
    }

    function setSubtraktionIndicator(height: number, width: number)
    {
        if (height <= 100 && width <= 100) {
            return 0;
        }
        if (((height >= 100 && height <= 200) && (width >= 100 && width <= 200))) {
            return 1.5;
        }
        else {
            return (Math.floor(width / (width / 10)));
        }
    }

    useEffect(() =>
    {
        if (current) {
            const { image } = current;
            setFloor({ ...current, ..._floor });
            loadImageBounds(image!, (size) =>
            {
                const [height, width] = size;

                const imageSubtraktionIndicator = setSubtraktionIndicator(height, width);
                setSize({ width: width / imageSubtraktionIndicator, height: height / imageSubtraktionIndicator });
            });
        }
    }, [current]);

    return <IonModal
        ref={modal}
        className={style.newSystemModal}
        isOpen={_floor && "label" in _floor}
        onWillDismiss={onWillDismiss}>
        <IonHeader>
            <IonToolbar>
                <div
                    className={style.header}
                >
                    <IonTitle>{current?.label}</IonTitle>
                </div>
            </IonToolbar>
        </IonHeader>

        <IonList
            className="ion-padding"
            color="primary">
            <SettingsTextInput
                text={"COM_ADMIN_USER_HEADING_NAME"}
                value={_floor?.label || ""}
                onChange={(label) =>
                {
                    setError(!label || !label.length ? t("[REACT_PLEASE_ENTER_A_NAME]") : '');
                    setFloor({ ..._floor, label });
                }}
            />
            <SettingsNumberInput
                text={"COM_FACPMANAGEMENT_MAPS_LEVEL"}
                value={_floor?.floor || 0}
                onChange={(floor) =>
                {
                    setError(floorNumberExist(floor) ? t("[REACT_ALREADY_EXIST]") : '');
                    setFloor({ ..._floor, floor });
                }} />

            <SettingsCheckbox
                disabled={!isMainPlan(current) && hasMainPlan(floors)}
                text={"[REACT_MAIN_PLAN]"}
                checked={_floor?.isMain || false}
                onChange={(value) =>
                {
                    setFloor({ ..._floor, isMain: value });
                }
                } />

            {_floor?.image ?
                <IonItem
                    style={{ minHeight: "143px" }}
                >
                    <IonFab vertical="top" horizontal="end">
                        <IonFabButton
                            color={"danger"}
                            size="small"
                            onClick={() => setFloor({ ..._floor, image: undefined })}
                        >
                            <IonIcon icon={trash} />
                        </IonFabButton>
                    </IonFab>
                    <div className={style.image}>
                        <img
                            className={style.img}
                            width={(size?.width || 100)}
                            height={(size?.height || 100)}
                            src={_floor.image}
                            loading="lazy"
                            alt=""
                        />
                    </div>
                </IonItem>
                :
                <SettingsDropFile
                    text={"[REACT_PLAN_IMAGE]"}
                    accept="image/*"
                    maxSize={8_000_000}
                    info="JGLOBAL_MAXIMUM_UPLOAD_SIZE_LIMIT"
                    //onError={(error) => console.log(error)}
                    onFile={function (file: File): void
                    {
                        // TODO: limit?

                        try {
                            const reader = new FileReader();
                            reader.onload = (e) =>
                            {
                                const base64String = e.target?.result;
                                if (base64String) {
                                    setFloor({ ..._floor, image: base64String.toString() });
                                    if (error.length) setError("");
                                }
                            };
                            reader.readAsDataURL(file);
                        }
                        catch (err) {
                            setError(t("[REACT_THIS_FILE_CAN_NOT_BE_UPLOADED]"));
                        }
                    }}
                />
            }
            <IonItem lines={error.length ? "full" : "none"}>
                <div className={style.error}>
                    <IonText color={"danger"}>{error}</IonText>
                </div>
            </IonItem>
        </IonList>
        <div className={`${style.buttonContainer} ${style.marginBottom}`}>
            <IonButton
                slot="start"
                color={"danger"}
                onClick={() => close()}>
                {t("COM_BANNERS_CANCEL")}
            </IonButton>
            <IonButton
                color={"danger"}
                onClick={() => _delete()}>
                {t("COM_CONTENTHISTORY_BUTTON_DELETE")}
            </IonButton>
            {!isMainPlan(current) && current?.labelPositionX && current.labelPositionY ? <IonButton
                color={"warning"}
                onClick={() =>
                {
                    const labelPositionX = null;
                    const labelPositionY = null;

                    const data: any = { ...current, ...{ labelPositionX, labelPositionY } };
                    if (onModified) { onModified(data); }
                }}
            >{t("[REACT_CLEAR_POSITION]")} </IonButton> : null}
            <IonButton
                slot="end"
                color={"success"}
                onClick={() =>
                {
                    const data = { ...current, ..._floor } as SystemPlan;
                    if (onModified) { onModified(data); }
                    close();
                }}>
                {t("COM_FACPMANAGEMENT_USER_SAVE")}
            </IonButton>
        </div>
    </IonModal>;

};


export default FloorEdit;
